import React from 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import App from './App.tsx';
import './index.css';
import './lib/posthog';

// Add console log to help debug production issues
console.log('Main.tsx loaded, environment:', import.meta.env.MODE);

// Add error boundary
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    console.error('ErrorBoundary caught error:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('=== ERROR BOUNDARY CAUGHT ERROR ===');
    console.error('Error:', error);
    console.error('Error name:', error?.name);
    console.error('Error message:', error?.message);
    console.error('Error stack:', error?.stack);
    console.error('Component stack:', errorInfo?.componentStack);
    console.error('Current URL:', window.location.href);
    console.error('=================================');
  }

  render() {
    if (this.state.hasError) {
      const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
      const error = this.state.error;

      return (
        <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
          <div className="text-center max-w-2xl mx-auto">
            <h1 className="text-2xl font-bold text-primary-900 mb-4">Something went wrong</h1>
            <p className="text-primary-700 mb-4">
              We encountered an error while loading the page. This might be a temporary issue.
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                <div className="text-sm font-semibold text-red-800 mb-2">Error Message:</div>
                <div className="text-sm text-red-700 font-mono break-words">{error.message || 'Unknown error'}</div>
              </div>
            )}
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-accent-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-accent-600 transition-colors"
              >
                Refresh Page
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="w-full border border-neutral-300 text-primary-700 px-6 py-3 rounded-lg font-medium hover:bg-neutral-50 transition-colors"
              >
                Go to Homepage
              </button>
            </div>
            {isDev && error && (
              <details className="mt-6 text-left">
                <summary className="text-sm text-primary-600 cursor-pointer font-semibold">
                  🔍 Error Details (Development Mode)
                </summary>
                <div className="mt-4 space-y-2">
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <div className="text-xs font-bold text-red-800 mb-1">Error Type:</div>
                    <div className="text-xs text-red-700">{error.name || 'Unknown'}</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <div className="text-xs font-bold text-red-800 mb-1">Error Message:</div>
                    <div className="text-xs text-red-700">{error.message || 'No message available'}</div>
                  </div>
                  {error.stack && (
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <div className="text-xs font-bold text-red-800 mb-1">Stack Trace:</div>
                      <pre className="text-xs text-red-700 overflow-auto max-h-64">
                        {error.stack}
                      </pre>
                    </div>
                  )}
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <div className="text-xs font-bold text-yellow-800 mb-1">Current URL:</div>
                    <div className="text-xs text-yellow-700 break-all">{window.location.href}</div>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Add global error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  
  // Check if the error is related to invalid login credentials
  const errorMessage = event.reason?.message || event.reason?.toString() || '';
  if (errorMessage.includes('Invalid login credentials')) {
    console.log('Invalid credentials detected, clearing session and reloading...');
    // Dynamically import Supabase client and clear the session
    import('./lib/supabase').then(({ supabase }) => {
      supabase.auth.signOut().then(() => {
        window.location.reload();
      });
    }).catch((importError) => {
      console.error('Failed to import Supabase:', importError);
      // Fallback: just reload the page to clear any cached auth state
      window.location.reload();
    });
  }
  
  // Prevent the default browser behavior
  event.preventDefault();
});

// Add global error handler
window.addEventListener('error', (event) => {
  console.error('Global error:', event.error);
});

// Check if root element exists
const rootElement = document.getElementById('root');
if (!rootElement) {
  console.error('Root element not found!');
  document.body.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; font-family: system-ui;">
      <div style="text-align: center; max-width: 400px; padding: 20px;">
        <h1 style="color: #1e293b; margin-bottom: 16px;">Application Error</h1>
        <p style="color: #64748b; margin-bottom: 20px;">The application could not start properly. Please refresh the page.</p>
        <button onclick="window.location.reload()" style="background: #f08030; color: white; padding: 12px 24px; border: none; border-radius: 8px; cursor: pointer;">
          Refresh Page
        </button>
      </div>
    </div>
  `;
} else {
  const root = createRoot(rootElement);

  root.render(
    <StrictMode>
      <HelmetProvider>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </HelmetProvider>
    </StrictMode>
  );
}